/* ITT MOTION ENGINE — theme-level · FRONT DOOR V2 (Oct 30, 2026).
   ONE permission grant (the hero "Motion" toggle), ONE deviceorientation
   listener, ONE rAF loop for the whole page. It drives:
     (a) tilt subscribers (hero 3D camera + hero glare, via ITTMotion.onTilt),
     (b) parallax depth on decorative layers page-wide — [data-tilt] elements
         plus built-in targets (hub stage, Audi banner video, new-drops card
         imagery). Motion is applied through the independent CSS `translate`
         / `scale` properties, so it NEVER overrides an element's own
         transform (the hub ring keeps its rotation; cards keep their tilt).
     (c) TILT-SCROLL ASSIST — leaning past a dead zone gently drifts the page
         at a capped speed. A finger ALWAYS wins: assist fully disengages
         while touching and for 1s after. It also disengages when an input
         is focused (forms), when a cart drawer / open dialog is visible,
         and it never exists on Shopify checkout (checkout does not load
         this layout). Body text is never a target and never moves.
   Hard rules: prefers-reduced-motion → the engine refuses to start.
   Tab hidden → loop pauses. OFF → every listener removed, every applied
   translate cleared. Desktop (no orientation sensor) → pointer position
   drives the same tilt signal; the toggle still gates everything. */
(function () {
  'use strict';
  if (window.ITTMotion) return;
  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var on = false, raf = 0, lastT = 0;
  var raw = { x: 0, y: 0 }, cur = { x: 0, y: 0 };
  var neutral = null;              /* pose captured on the first event after enable */
  var lastOrientAt = 0;
  var subs = [];
  var targets = [];                /* {el, depth, scale} */
  var touching = false, lastTouchEnd = 0;
  var guard = { t: 0, blocked: false };
  var assistActive = false;
  var hintShown = false;
  var hubSeen = null;
  var frameCount = 0;

  var DEAD = 0.34;                 /* tilt dead zone, normalised */
  var MAXSCROLL = 460;             /* px per second at full lean — capped, gentle */
  function clamp(v) { return Math.max(-1, Math.min(1, v)); }

  /* ---------------- targets ---------------- */
  function addTarget(el, depth, scale) {
    if (!el) return;
    for (var i = 0; i < targets.length; i++) if (targets[i].el === el) return;
    targets.push({ el: el, depth: depth, scale: scale || 0 });
    if (scale) el.style.scale = String(scale);
  }
  function collectTargets() {
    targets = [];
    var attr = document.querySelectorAll('[data-tilt]');
    for (var i = 0; i < attr.length; i++) {
      var d = parseFloat(attr[i].getAttribute('data-tilt'));
      var sc = parseFloat(attr[i].getAttribute('data-tilt-scale'));
      addTarget(attr[i], isFinite(d) ? d : 4, isFinite(sc) ? sc : 0);
    }
    addTarget(document.getElementById('ittHub'), 3, 1.012);            /* hub stage drift (ring transform untouched) */
    addTarget(document.querySelector('.iap-video'), 4, 1.06);          /* Audi banner video */
    var imgs = document.querySelectorAll('.wl-drop img');              /* new-drops card imagery */
    for (var j = 0; j < imgs.length; j++) addTarget(imgs[j], 3, 1.05);
    hubSeen = !!document.getElementById('ittHub');
  }
  function clearTargets() {
    for (var i = 0; i < targets.length; i++) {
      targets[i].el.style.translate = '';
      if (targets[i].scale) targets[i].el.style.scale = '';
    }
    targets = [];
  }

  /* ---------------- sensors ---------------- */
  function onOrient(e) {
    if (e.beta == null || e.gamma == null) return;
    lastOrientAt = performance.now();
    if (!neutral) neutral = { beta: e.beta, gamma: e.gamma };
    raw.x = clamp((e.gamma - neutral.gamma) / 26);
    raw.y = clamp((e.beta - neutral.beta) / 26);
  }
  function onPointer(e) {
    if (performance.now() - lastOrientAt < 2000) return;   /* a real sensor wins */
    raw.x = clamp((e.clientX / window.innerWidth) * 2 - 1);
    raw.y = clamp((e.clientY / window.innerHeight) * 2 - 1);
  }
  function onTouchStart() { touching = true; }
  var touchEnded = false;
  function onTouchEnd() { touching = false; touchEnded = true; }
  function onVis() {
    if (document.hidden) { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
    else if (on && !raf) { lastT = performance.now(); raf = requestAnimationFrame(frame); }
  }

  /* ---------------- tilt-scroll assist ---------------- */
  function assistBlocked(now) {
    if (touching || now - lastTouchEnd < 1000) return true;
    var ae = document.activeElement;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable)) return true;
    if (now - guard.t > 500) {                      /* drawer/dialog check, throttled */
      guard.t = now;
      guard.blocked = false;
      var d = document.querySelector('[data-cart-drawer], cart-drawer, .cart-drawer, dialog[open]');
      if (d) { var r = d.getBoundingClientRect(); guard.blocked = r.width > 0 && r.height > 0; }
    }
    return guard.blocked;
  }
  function assist(dt, now) {
    assistActive = false;
    if (Math.abs(cur.y) < DEAD) return;
    if (assistBlocked(now)) return;
    var over = (Math.abs(cur.y) - DEAD) / (1 - DEAD);
    window.scrollBy(0, Math.sign(cur.y) * over * MAXSCROLL * dt);
    assistActive = true;
  }

  /* ---------------- the one loop ---------------- */
  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (touchEnded) { touchEnded = false; lastTouchEnd = now; }  /* stamp in the rAF clock domain — the same clock assistBlocked compares against */
    var dt = Math.min(0.25, lastT ? (now - lastT) / 1000 : 0.016);
    lastT = now;
    var k = Math.min(1, 1 - Math.pow(0.001, dt));       /* smooth damp */
    cur.x += (raw.x - cur.x) * k;
    cur.y += (raw.y - cur.y) * k;
    for (var i = 0; i < subs.length; i++) { try { subs[i](cur.x, cur.y, dt); } catch (e) {} }
    for (var j = 0; j < targets.length; j++) {
      var t = targets[j];
      t.el.style.translate = (-cur.x * t.depth).toFixed(2) + 'px ' + (-cur.y * t.depth).toFixed(2) + 'px';
    }
    assist(dt, now);
    if ((frameCount++ & 127) === 0) {                    /* hub injects late — re-collect if it appears */
      var has = !!document.getElementById('ittHub');
      if (has !== hubSeen) collectTargets();
    }
  }

  /* ---------------- hint ---------------- */
  function showHint() {
    if (hintShown) return; hintShown = true;
    var h = document.querySelector('[data-motion-hint]');
    if (!h) return;
    h.textContent = h.getAttribute('data-on-text') || 'Motion on — lean to look around. Lean forward to drift down the page; your finger always wins.';
    h.hidden = false;
    setTimeout(function () { h.hidden = true; }, 6000);
  }

  /* ---------------- enable / disable ---------------- */
  function start() {
    on = true; neutral = null; raw.x = 0; raw.y = 0; cur.x = 0; cur.y = 0;
    collectTargets();
    window.addEventListener('deviceorientation', onOrient);
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', onVis);
    lastT = performance.now();
    raf = requestAnimationFrame(frame);
    showHint();
  }
  function enable() {
    if (reduced) return Promise.resolve(false);
    if (on) return Promise.resolve(true);
    var DOE = window.DeviceOrientationEvent;
    if (DOE && typeof DOE.requestPermission === 'function') {   /* iOS: the single grant */
      return DOE.requestPermission().then(function (res) {
        if (res === 'granted') { start(); return true; }
        return false;
      }).catch(function () { return false; });
    }
    start();
    return Promise.resolve(true);
  }
  function disable() {
    if (!on) return;
    on = false; assistActive = false;
    window.removeEventListener('deviceorientation', onOrient);
    window.removeEventListener('pointermove', onPointer);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', onTouchEnd);
    document.removeEventListener('visibilitychange', onVis);
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
    clearTargets();
    raw.x = 0; raw.y = 0; cur.x = 0; cur.y = 0;
    for (var i = 0; i < subs.length; i++) { try { subs[i](0, 0, 0); } catch (e) {} }
  }

  window.ITTMotion = {
    enable: enable,
    disable: disable,
    toggle: function () { if (on) { disable(); return Promise.resolve(false); } return enable(); },
    isOn: function () { return on; },
    onTilt: function (cb) {
      subs.push(cb);
      return function () { var i = subs.indexOf(cb); if (i >= 0) subs.splice(i, 1); };
    },
    state: function () {
      return { on: on, x: +cur.x.toFixed(3), y: +cur.y.toFixed(3), assist: assistActive, targets: targets.length, reduced: reduced };
    }
  };
  window.__ittMotion = window.ITTMotion.state;   /* QA hook */
})();
